/**
 * KNN PDP Gallery
 * Enhancements for the main product page's vertical thumbnails list
 * (.product-information__media, left/right pagination only):
 *  - Mouse click-and-drag to scroll, since native drag-to-scroll only exists for touch.
 *  - Auto-hide the up/down step chevrons at the start/end of the slideshow.
 * Scoped to the main gallery; the quick-add modal already hides thumbnails entirely.
 * Uses event delegation (rather than binding listeners per-element) so it keeps working
 * if the gallery markup is later morphed in place, e.g. on variant image selection.
 */
(() => {
  const THUMBNAILS_CONTAINER_SELECTOR =
    '.product-information__media .slideshow-controls__thumbnails-container:not(.quick-add-modal *)';
  const DRAG_THRESHOLD = 5;

  document.addEventListener('pointerdown', (event) => {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    if (!(event.target instanceof Element)) return;

    const container = event.target.closest(THUMBNAILS_CONTAINER_SELECTOR);
    if (!container) return;

    const isVertical = container.scrollHeight > container.clientHeight;
    const isHorizontal = container.scrollWidth > container.clientWidth;
    if (!isVertical && !isHorizontal) return;

    const startX = event.clientX;
    const startY = event.clientY;
    const startScrollLeft = container.scrollLeft;
    const startScrollTop = container.scrollTop;
    const controller = new AbortController();
    const { signal } = controller;
    let dragging = false;

    const onPointerMove = (moveEvent) => {
      const deltaX = moveEvent.clientX - startX;
      const deltaY = moveEvent.clientY - startY;

      if (!dragging) {
        if (Math.abs(deltaX) < DRAG_THRESHOLD && Math.abs(deltaY) < DRAG_THRESHOLD) return;
        dragging = true;
        // Redirects the eventual click to the container (which has no on:click
        // handler), so dragging a thumbnail doesn't also select it.
        container.setPointerCapture(event.pointerId);
        container.classList.add('is-dragging');
      }

      moveEvent.preventDefault();
      if (isHorizontal) container.scrollLeft = startScrollLeft - deltaX;
      if (isVertical) container.scrollTop = startScrollTop - deltaY;
    };

    const onPointerUp = () => {
      controller.abort();
      if (!dragging) return;
      container.classList.remove('is-dragging');
      container.releasePointerCapture(event.pointerId);
    };

    document.addEventListener('pointermove', onPointerMove, { signal });
    document.addEventListener('pointerup', onPointerUp, { signal });
    document.addEventListener('pointercancel', onPointerUp, { signal });
  });

  document.addEventListener('slideshow:select', (event) => {
    const slideshowComponent = event.target;
    if (!(slideshowComponent instanceof HTMLElement)) return;
    if (!slideshowComponent.closest('.product-information__media')) return;

    slideshowComponent.querySelectorAll('.slideshow-controls__thumbnail-nav').forEach((button) => {
      const isPrevious = button.classList.contains('slideshow-controls__thumbnail-nav--previous');
      button.hidden = isPrevious ? Boolean(slideshowComponent.atStart) : Boolean(slideshowComponent.atEnd);
    });
  });
})();
